// ─── Colour system ────────────────────────────────────────────────────────────
// One brand hue (violet) carries every accent in the app: primary buttons,
// active nav, focus rings, selection, links and "in progress" states.
// Green / amber / red are reserved for status meaning only (success, warning,
// error). Everything else is neutral slate. To re-brand, change PRIMARY_SCALE.

export const PRIMARY_SCALE = {
  50: '#f5f3ff',
  100: '#ede9fe',
  200: '#ddd6fe',
  300: '#c4b5fd',
  400: '#a78bfa',
  500: '#8b5cf6',
  600: '#7c3aed',
  700: '#6d28d9',
  800: '#5b21b6',
  900: '#4c1d95',
  950: '#2e1065', // large brand surfaces (login brand panel)
} as const

// ─── Brand palette ────────────────────────────────────────────────────────────
export const COLORS = {
  PRIMARY: PRIMARY_SCALE[600],
  PRIMARY_HOVER: PRIMARY_SCALE[700],
  PRIMARY_LIGHT: PRIMARY_SCALE[400], // primary text/icons on dark surfaces
  PRIMARY_DARK: PRIMARY_SCALE[700], // primary text/icons on light surfaces
  PRIMARY_MID: PRIMARY_SCALE[500], // readable on both light and dark (static maps)

  // Status colours — only for success / warning / error meaning. The 600
  // shades stay readable as text on both the light and the dark surfaces.
  SUCCESS: '#059669',
  WARNING: '#d97706',
  ERROR: '#dc2626',

  // Neutral used for category icons, idle states and "other" data
  NEUTRAL: '#64748b',

  SLATE_50: '#f8fafc',
  SLATE_300: '#cbd5e1',
  SLATE_400: '#94a3b8',
  SLATE_500: '#64748b',
  SLATE_600: '#475569',
  SLATE_700: '#334155',
  SLATE_800: '#1e293b',
  BG_DARK: '#0b0f19',
  TEXT_LIGHT: '#f1f5f9',
  TEXT_CODE: '#e2e8f0',
  WHITE: '#fff',
  BLACK: '#000',
} as const

/** `#rrggbb` + alpha → `rgba()`; lets tints follow the palette instead of hard-coded rgb. */
export function withAlpha(hex: string, alpha: number): string {
  const h = hex.replace('#', '')
  const full =
    h.length === 3
      ? h
          .split('')
          .map((ch) => ch + ch)
          .join('')
      : h
  const n = parseInt(full, 16)
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`
}

// ─── Brand fills ──────────────────────────────────────────────────────────────
// Kept as named fills so call sites stay stable; they are solid now — the old
// violet→blue gradient added a second accent hue everywhere it was used.
export const GRADIENTS = {
  BRAND: COLORS.PRIMARY,
  BRAND_HOVER: COLORS.PRIMARY_HOVER,
  BRAND_H: COLORS.PRIMARY,
} as const

// ─── Semi-transparent tints (used in backgrounds / borders) ──────────────────
export const TINTS = {
  PRIMARY_08: withAlpha(COLORS.PRIMARY, 0.08),
  PRIMARY_15: withAlpha(COLORS.PRIMARY, 0.15),
  PRIMARY_BORDER: withAlpha(COLORS.PRIMARY, 0.3),
  PRIMARY_GLOW_SM: `0 0 12px ${withAlpha(COLORS.PRIMARY, 0.3)}`,
  OVERLAY: 'rgba(0,0,0,0.55)',
} as const

// ─── Z-index scale ────────────────────────────────────────────────────────────
export const Z = {
  SIDEBAR: 100,
  NAVBAR: 99,
  COMPACT_BAR: 50,
  AI_PANEL: 200,
  OVERLAY: 10,
} as const

// ─── Role badge colours ───────────────────────────────────────────────────────
export const ROLE_COLORS: Record<string, string> = {
  admin: COLORS.PRIMARY,
  manager: COLORS.PRIMARY,
  viewer: COLORS.NEUTRAL,
}

// ─── Font stacks ─────────────────────────────────────────────────────────────
export const FONTS = {
  SANS: '"Inter", "Segoe UI", system-ui, sans-serif',
  MONO_FULL:
    '"JetBrains Mono", "Fira Code", "Cascadia Code", "Consolas", monospace',
  CODE: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
  MONO: '"JetBrains Mono", "Fira Mono", "Cascadia Code", monospace',
  MONO_SIMPLE: '"JetBrains Mono", "Fira Mono", monospace',
  MONO_PLAIN: '"JetBrains Mono", monospace',
} as const

// ─── Layout sizes (px) ───────────────────────────────────────────────────────
export const SIZES = {
  NAVBAR_HEIGHT: 56, // header row inside the page sheet
  SHELL_GAP: 8, // gap between the page sheet, the AI panel and the window edge
  SIDEBAR_COLLAPSED: 64,
  SIDEBAR_DEFAULT: 240,
  SIDEBAR_PROJECT: 300,
  AI_PANEL: 400,
  // Below this viewport width the AI panel overlays the page instead of
  // docking beside it, so the content never gets squeezed too narrow.
  AI_PANEL_DOCK_MIN: 1100,
} as const
